import React, { useState, useEffect, useCallback } from "react";
import ApiService from "../services/ApiService";
import "./CouponManagement.css";

const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    isActive: null,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const [newCoupon, setNewCoupon] = useState({
    code: "",
    discountAmount: "",
    usageLimit: 1,
    isActive: true,
  }); // Fetch coupons
  const fetchCoupons = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        const data = await ApiService.getAllCoupons(
          page,
          pagination.limit,
          filters.search,
          filters.isActive
        );

        if (data.success) {
          setCoupons(data.data.coupons);
          setPagination(data.data.pagination);
        } else {
          setError(data.message || "Failed to fetch coupons");
        }
      } catch (error) {
        console.error("Error fetching coupons:", error);
        setError(error.message || "Failed to fetch coupons");
      } finally {
        setLoading(false);
      }
    },
    [pagination.limit, filters.search, filters.isActive]
  );
  // Create coupon
  const createCoupon = async (e) => {
    e.preventDefault();
    try {
      const data = await ApiService.createCoupon(newCoupon);

      if (data.success) {
        setSuccess("Coupon created successfully");
        setShowCreateModal(false);
        setNewCoupon({
          code: "",
          discountAmount: "",
          usageLimit: 1,
          isActive: true,
        });
        fetchCoupons(pagination.page);
      } else {
        setError(data.message || "Failed to create coupon");
      }
    } catch (error) {
      console.error("Error creating coupon:", error);
      setError(error.message || "Failed to create coupon");
    }
  }; // Update coupon
  const updateCoupon = async (e) => {
    e.preventDefault();
    try {
      // Only send editable fields - exclude system-generated fields
      const couponUpdateData = {
        code: editingCoupon.code,
        discountAmount: editingCoupon.discountAmount,
        usageLimit: editingCoupon.usageLimit,
        isActive: editingCoupon.isActive,
      };

      const data = await ApiService.updateCoupon(
        editingCoupon.id,
        couponUpdateData
      );

      if (data.success) {
        setSuccess("Coupon updated successfully");
        setEditingCoupon(null);
        fetchCoupons(pagination.page);
      } else {
        setError(data.message || "Failed to update coupon");
      }
    } catch (error) {
      console.error("Error updating coupon:", error);
      setError(error.message || "Failed to update coupon");
    }
  };

  // Delete coupon
  const deleteCoupon = async (couponId) => {
    if (!window.confirm("Are you sure you want to delete this coupon?")) return;
    try {
      const data = await ApiService.deleteCoupon(couponId);

      if (data.success) {
        setSuccess("Coupon deleted successfully");
        fetchCoupons(pagination.page);
      } else {
        setError(data.message || "Failed to delete coupon");
      }
    } catch (error) {
      console.error("Error deleting coupon:", error);
      setError(error.message || "Failed to delete coupon");
    }
  };

  // Handle search
  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchCoupons(1);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      search: "",
      isActive: null,
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
    setTimeout(() => fetchCoupons(1), 100);
  };
  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess("");
        setError("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  return (
    <div className="coupon-management">
      <div className="admin-header-section">
        <div>
          <h1>Coupon Management</h1>
          <p>Create and manage discount coupons</p>
        </div>
        <button
          className="create-coupon-btn"
          onClick={() => setShowCreateModal(true)}
        >
          <span>+</span>
          Create Coupon
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Filters */}
      <div className="coupon-filters">
        <div className="filter-form">
          <div className="filter-row">
            <div className="filter-group">
              <label>Search Coupons</label>
              <input
                type="text"
                placeholder="Search by coupon code..."
                className="search-input"
                value={filters.search}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, search: e.target.value }))
                }
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>

            <div className="filter-group">
              <label>Status</label>
              <select
                className="filter-select"
                value={filters.isActive || ""}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    isActive:
                      e.target.value === "" ? null : e.target.value === "true",
                  }))
                }
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            <div className="filter-actions">
              <button className="search-btn" onClick={handleSearch}>
                Search
              </button>
              <button className="clear-btn" onClick={clearFilters}>
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        <p>
          Showing {coupons.length} of {pagination.total} coupons
        </p>
      </div>

      {/* Coupons Table */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading coupons...</p>
        </div>
      ) : coupons.length === 0 ? (
        <div className="no-coupons-message">
          <h3>No coupons found</h3>
          <p>Create your first coupon to get started.</p>
        </div>
      ) : (
        <div className="coupons-table-wrapper">
          <table className="coupons-table">
            <thead>
              <tr>
                <th>Coupon Code</th>
                <th>Discount Amount</th>
                <th>Usage Limit</th>
                <th>Used Count</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((coupon) => (
                <tr key={coupon.id}>
                  <td>
                    <div className="coupon-code">
                      <span className="code-text">{coupon.code}</span>
                    </div>
                  </td>
                  <td>
                    <div className="discount-amount">
                      ₹{parseFloat(coupon.discountAmount).toFixed(2)}
                    </div>
                  </td>
                  <td>
                    <div className="usage-limit">{coupon.usageLimit}</div>
                  </td>
                  <td>
                    <div className="used-count">
                      <span
                        className={
                          coupon.usedCount >= coupon.usageLimit ? "used-up" : ""
                        }
                      >
                        {coupon.usedCount}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span
                      className={`status-badge ${
                        coupon.isActive ? "active" : "inactive"
                      }`}
                    >
                      {coupon.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div className="created-date">
                      {new Date(coupon.createdAt).toLocaleDateString()}
                    </div>
                  </td>
                  <td>
                    <div className="coupon-actions">
                      <button
                        className="edit-btn"
                        onClick={() => setEditingCoupon({ ...coupon })}
                      >
                        Edit
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => deleteCoupon(coupon.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            disabled={pagination.page <= 1}
            onClick={() => fetchCoupons(pagination.page - 1)}
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            className="pagination-btn"
            disabled={pagination.page >= pagination.pages}
            onClick={() => fetchCoupons(pagination.page + 1)}
          >
            Next
          </button>
        </div>
      )}

      {/* Create Coupon Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Create New Coupon</h3>
              <button
                className="close-btn"
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={createCoupon}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Coupon Code *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g., SAVE100"
                    value={newCoupon.code}
                    onChange={(e) =>
                      setNewCoupon((prev) => ({
                        ...prev,
                        code: e.target.value.toUpperCase(),
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Discount Amount (₹) *</label>
                  <input
                    type="number"
                    required
                    min="0.01"
                    step="0.01"
                    placeholder="e.g., 100.00"
                    value={newCoupon.discountAmount}
                    onChange={(e) =>
                      setNewCoupon((prev) => ({
                        ...prev,
                        discountAmount: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Usage Limit *</label>
                  <input
                    type="number"
                    required
                    min="1"
                    placeholder="e.g., 100"
                    value={newCoupon.usageLimit}
                    onChange={(e) =>
                      setNewCoupon((prev) => ({
                        ...prev,
                        usageLimit: parseInt(e.target.value) || 1,
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={newCoupon.isActive}
                      onChange={(e) =>
                        setNewCoupon((prev) => ({
                          ...prev,
                          isActive: e.target.checked,
                        }))
                      }
                    />
                    Active
                  </label>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="save-btn">
                  Create Coupon
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Coupon Modal */}
      {editingCoupon && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Coupon</h3>
              <button
                className="close-btn"
                onClick={() => setEditingCoupon(null)}
              >
                ×
              </button>
            </div>
            <form onSubmit={updateCoupon}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Coupon Code *</label>
                  <input
                    type="text"
                    required
                    value={editingCoupon.code}
                    onChange={(e) =>
                      setEditingCoupon((prev) => ({
                        ...prev,
                        code: e.target.value.toUpperCase(),
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Discount Amount (₹) *</label>
                  <input
                    type="number"
                    required
                    min="0.01"
                    step="0.01"
                    value={editingCoupon.discountAmount}
                    onChange={(e) =>
                      setEditingCoupon((prev) => ({
                        ...prev,
                        discountAmount: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Usage Limit *</label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={editingCoupon.usageLimit}
                    onChange={(e) =>
                      setEditingCoupon((prev) => ({
                        ...prev,
                        usageLimit: parseInt(e.target.value) || 1,
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={editingCoupon.isActive}
                      onChange={(e) =>
                        setEditingCoupon((prev) => ({
                          ...prev,
                          isActive: e.target.checked,
                        }))
                      }
                    />
                    Active
                  </label>
                </div>

                <div className="coupon-stats">
                  <p>
                    <strong>Used Count:</strong> {editingCoupon.usedCount}
                  </p>
                  <p>
                    <strong>Created:</strong>{" "}
                    {new Date(editingCoupon.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setEditingCoupon(null)}
                >
                  Cancel
                </button>
                <button type="submit" className="save-btn">
                  Update Coupon
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponManagement;
